import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs,
    Adg4TabPanel
} from "@/components/adg4/tabs/src";
import Link, { LinkType } from "@/components/wpl/link/src";
import {
    DataEventProps,
    ProductKeyTypes,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

export interface PatternADG4TestimonialProps {
    bgColor?: "blue" | "black" | "white";
    heading?: string;
    subheading?: string;
    cta?: {
        text: string;
        url: string;
        dataEventProps?: DataEventProps;
    };
    testimonials: TestimonialsTypes[];
    productKey?: ProductKeyTypes;
    className?: string;
    imagePosition?: "left" | "right";
    testimonialCta?: ReactNode;
}

export type TestimonialsTypes = {
    eyebrow?: string;
    logo: JSX.Element;
    name: string;
    title: string;
    image: string;
    quote: string;
    link?: {
        text: string;
        url: string;
        type?: LinkType;
        dataEventProps?: DataEventProps;
    };
    label: string;
};

const AUTO_TRANSITION_INTERVAL = 8000;

export const ADG4CustomerTestimonials = ({
    bgColor = "blue",
    heading,
    subheading,
    cta,
    testimonials,
    productKey,
    className,
    imagePosition = "left",
    testimonialCta
}: PatternADG4TestimonialProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const logosContainerStyles = css({
        "display": testimonialCta ? "flex" : "block",
        "justifyContent": "space-between",
        "@media (max-width: 1140px)": {
            flexDirection: "column"
        },
        ".logos": {
            "margin-left": testimonialCta ? "0" : "auto"
        }
    });

    return (
        <Adg4Tabs tabSwitchInterval={AUTO_TRANSITION_INTERVAL} loop={true}>
            <TypographyStyles>
                <section
                    ref={scrollRef}
                    className={sectionIsVisible ? "is-visible" : ""}
                >
                    {!heading && !subheading && !cta ? null : (
                        <Row layout="8">
                            <div css={headerStyles}>
                                {heading && <h2>{heading}</h2>}
                                {subheading && (
                                    <p className="body-lg">{subheading}</p>
                                )}
                                {cta && (
                                    <Link
                                        text={cta.text}
                                        href={cta.url}
                                        dataEventProps={cta.dataEventProps}
                                        type={"basic"}
                                        doesArrowAnimate={true}
                                    />
                                )}
                            </div>
                        </Row>
                    )}

                    <div
                        css={testimonialStyles}
                        ref={scrollRef}
                        className={`${className} ${
                            sectionIsVisible ? "is-visible" : ""
                        } image-${imagePosition} ${bgColor}-background`}
                    >
                        <Row>
                            <div>
                                <div className="bgshape" />
                                <div css={tabPanelWrapperStyles}>
                                    {testimonials.map((testimonial, index) => {
                                        return (
                                            <Adg4TabPanel
                                                id={`${index}`}
                                                panelIndex={index}
                                                key={index}
                                                forceRender={true}
                                            >
                                                <div className="row-width top-row">
                                                    <div className="customer-images-section">
                                                        <img
                                                            src={
                                                                testimonial.image
                                                            }
                                                            alt={
                                                                testimonial.title
                                                            }
                                                        />
                                                    </div>
                                                    <div className="customer-quotes-section">
                                                        <div className="testimonial-quotes-section">
                                                            <div
                                                                className="quote-container"
                                                                key={index}
                                                            >
                                                                {testimonial.eyebrow && (
                                                                    <p className="eyebrow-md">
                                                                        {
                                                                            testimonial.eyebrow
                                                                        }
                                                                    </p>
                                                                )}
                                                                <p className="callout">
                                                                    “
                                                                    {
                                                                        testimonial.quote
                                                                    }
                                                                    ”
                                                                </p>
                                                                <p>
                                                                    <span className="eyebrow-lg">
                                                                        {
                                                                            testimonial.name
                                                                        }
                                                                    </span>
                                                                    <br />
                                                                    <span className="body-md title">
                                                                        {
                                                                            testimonial.title
                                                                        }
                                                                    </span>
                                                                </p>
                                                                {testimonial.link && (
                                                                    <Link
                                                                        customLinkColor="#fff"
                                                                        text={
                                                                            testimonial
                                                                                .link
                                                                                .text
                                                                        }
                                                                        href={
                                                                            testimonial
                                                                                .link
                                                                                .url
                                                                        }
                                                                        type={
                                                                            testimonial
                                                                                .link
                                                                                .type ??
                                                                            "basic"
                                                                        }
                                                                        bgColor="bgDark"
                                                                        dataEventProps={
                                                                            testimonial
                                                                                .link
                                                                                .dataEventProps
                                                                        }
                                                                        doesArrowAnimate={
                                                                            true
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Adg4TabPanel>
                                        );
                                    })}
                                </div>

                                <div className="row-width">
                                    <div
                                        css={logosContainerStyles}
                                        className="logos-container"
                                    >
                                        <Adg4DraggableTabList className="logos">
                                            <div className="top-line" />
                                            {testimonials.map(
                                                (testimonial, index) => {
                                                    return (
                                                        <Adg4DraggableTab
                                                            className="logo-option"
                                                            key={index}
                                                            tabIndex={index}
                                                        >
                                                            <div className="top-marker" />
                                                            <button
                                                                aria-label={
                                                                    testimonial.label
                                                                }
                                                                title={
                                                                    testimonial.label
                                                                }
                                                                data-product-key={
                                                                    productKey
                                                                }
                                                                data-event-component="button"
                                                                data-event-container="testimonialsSection"
                                                                data-event="clicked"
                                                                data-label-english={
                                                                    testimonial.label
                                                                }
                                                            >
                                                                <div className="logo-img-container">
                                                                    {
                                                                        testimonial.logo
                                                                    }
                                                                </div>
                                                            </button>
                                                        </Adg4DraggableTab>
                                                    );
                                                }
                                            )}
                                        </Adg4DraggableTabList>
                                        {testimonialCta && testimonialCta}
                                    </div>
                                </div>
                            </div>
                        </Row>
                    </div>
                </section>
            </TypographyStyles>
        </Adg4Tabs>
    );
};

const headerStyles = css({
    textAlign: "center",
    h2: {
        marginBlock: "40px 12px"
    },
    p: {
        marginBlock: "0 12px"
    },
    marginBlockEnd: "40px"
});

const testimonialStyles = css({
    "@keyframes fadeswipe": {
        from: {
            opacity: 0,
            transform: "translateX(100px)"
        },
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },
    "@keyframes fadeswipesmall": {
        from: {
            opacity: 0,
            transform: "translateX(20px)"
        },
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },

    "@keyframes fadein": {
        from: {
            opacity: 0
        },
        to: {
            opacity: 1
        }
    },

    "&.blue-background, &.black-background": {
        "color": "#fff",
        "p, span": {
            color: "#fff"
        },

        ".top-line": {
            backgroundColor: "#fff"
        },

        ".logo-option": {
            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#fff"
                }
            },
            "svg": {
                path: {
                    fill: "#fff"
                }
            }
        }
    },

    "&.blue-background": {
        ".bgshape": {
            background: "#1868DB"
        },

        ".top-line": {
            backgroundColor: "lightblue"
        },

        ".logo-option": {
            borderColor: "lightblue !important"
        }
    },

    "&.black-background": {
        ".bgshape": {
            background: "#000"
        }
    },

    "&.white-background": {
        ".bgshape": {
            background: "#fff"
        },

        ".top-line": {
            backgroundColor: "#000"
        },

        ".logo-option": {
            "borderColor": "black !important",

            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#000"
                }
            },

            "svg": {
                path: {
                    fill: "#000"
                }
            }
        }
    },

    "position": "relative",
    "zIndex": 1,
    "marginBlockEnd": token("space.500"),
    "&.image-left": {
        ".bgshape": {
            [media.below.sm]: {
                clipPath: "polygon(0 10%, 100% 0, 100% 100%, 0% 100%)"
            },
            clipPath: "polygon(2% 16%, 100% 0, 100% 100%, 0% 100%)"
        }
    },
    "&.image-right": {
        ".top-row": {
            [media.above.md]: {
                ".customer-images-section": {
                    gridColumnStart: "2"
                },

                ".customer-quotes-section": {
                    gridColumnStart: "1",
                    gridRowStart: "1"
                }
            }
        },
        ".bgshape": {
            [media.below.sm]: {
                clipPath: "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)"
            },
            clipPath: "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)"
        }
    },
    ".bgshape": {
        position: "absolute",
        inset: 0,
        width: "95%",
        maxWidth: "1368px",
        margin: "auto",

        [media.below.sm]: {
            width: "100%"
        }
    },
    ".row-width": {
        "position": "relative",
        "display": "flex",
        "rowGap": token("space.250"),
        "flexWrap": "wrap",
        "alignItems": "stretch",
        [media.below.sm]: {
            flexDirection: "column",
            textAlign: "center"
        },

        "&.top-row": {
            marginBlockEnd: "40px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: "32px",

            [media.below.md]: {
                gridTemplateColumns: "1fr"
            }
        },

        ".customer-images-section": {
            position: "relative",
            flexBasis: "40%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",

            [media.only.sm]: {
                flexBasis: "50%"
            },

            [media.below.sm]: {
                width: "90%",
                margin: "0 auto"
            },
            [media.only.xs]: {
                width: "70%",
                margin: "0 auto"
            },
            img: {
                height: "210px",

                [media.above.sm]: {
                    height: "258px"
                },

                [media.above.md]: {
                    height: "365px"
                },

                width: "auto",
                display: "block"
            }
        },
        ".customer-quotes-section": {
            [media.below.md]: {
                flexBasis: "50%"
            },
            flexBasis: "60%"
        },
        ".testimonial-quotes-section": {
            "display": "flex",
            "flexDirection": "column",
            "height": "100%",
            "justifyContent": "end",
            "borderRadius": "20px",
            "position": "relative",
            "margin": "0 auto",

            [media.above.md]: {
                paddingBlockStart: "80px"
            },

            [media.below.md]: {
                width: "66%"
            },

            [media.below.sm]: {
                width: "100%"
            },

            ".quote-container": {
                transition: "opacity 1s",
                display: "flex",
                flexDirection: "column",
                gap: "24px",

                p: {
                    marginBlock: "0"
                }
            }
        },

        ".logos-container": {
            overflow: "hidden",
            width: "100%",
            [media.below.sm]: {
                marginBlockStart: "20px"
            }
        },

        ".logos": {
            "display": "flex",
            "gap": "32px",
            "marginBlockEnd": "40px",
            "paddingBlockStart": "1px",
            "listStyle": "none",

            ".top-line": {
                position: "absolute",
                insetBlockStart: "1px",
                insetInlineStart: "0",
                insetInlineEnd: "0",
                height: "1px",
                borderRadius: "2px",
                opacity: "50%",

                [media.below.sm]: {
                    insetBlockStart: "21px"
                }
            },

            ".logo-option": {
                "flexBasis": "0%",
                "display": "flex",
                "justifyContent": "stretch",
                "alignItems": "stretch",
                "position": "relative",

                "&[aria-selected=true]": {
                    "paddingBlockStart": "0",

                    "svg": {
                        opacity: 1
                    },
                    ".top-marker": {
                        opacity: "1",
                        position: "absolute",
                        insetBlockStart: "-3px",
                        insetInlineStart: "0",
                        insetInlineEnd: "0",
                        height: "5px",
                        borderRadius: "2px"
                    }
                },

                ".logo-img-container": {
                    height: "34px",
                    display: "flex",
                    alignItems: "center"
                },

                "button": {
                    "background": "none",
                    "border": 0,
                    "appearance": "none",
                    "paddingBlockStart": "20px",
                    "cursor": "pointer",
                    "position": "relative",

                    "svg": {
                        pointerEvents: "none",
                        opacity: 0.5
                    },
                    "&:hover": {
                        svg: {
                            transition: "0.5s",
                            opacity: "1"
                        }
                    }
                }
            }
        }
    }
});

const tabPanelWrapperStyles = css({
    "display": "grid",
    "gridTemplateColumns": "1fr",
    "gridTemplateRows": "1fr",

    ".tab-panel": {
        "opacity": 0,
        "zIndex": 1,
        "display": "flex",
        "gridArea": "1 / 1",

        "&.active-panel": {
            "opacity": 1,
            "zIndex": 2,

            ".customer-images-section": {
                img: {
                    animation: "fadein 1s ease-out 1 forwards"
                }
            },

            ".quote-container": {
                animation: "fadeswipe 1s 1",

                [media.below.sm]: {
                    animation: "fadeswipesmall 1s 1"
                }
            }
        }
    }
});
